import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { api } from "../lib/api";
import { track, EVENTS } from "../lib/analytics";
import {
  KINVANTA_LOGO_URL, KINVANTA_RELATIONS, KINVANTA_NEEDS,
} from "../lib/kinvanta";
import { useAuth } from "../lib/auth";

/**
 * Reusable Kinvanta Early Access card + modal.
 *
 * Props:
 *   variant: "sidecard" (Dashboard) | "module" (Settings) | "footer" (Landing)
 *   source: analytics source_page tag
 */
export function KinvantaCard({ variant = "sidecard", source = "unknown" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    track(EVENTS.KINVANTA_WAITLIST_VIEWED, { source, variant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (variant === "footer") {
    return (
      <>
        <div
          className="rounded-3xl border border-border/60 bg-white/70 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 soft-shadow"
          data-testid={`kinvanta-footer-${source}`}
        >
          <img
            src={KINVANTA_LOGO_URL}
            alt="Kinvanta by PaxBond"
            className="h-10 md:h-12 w-auto object-contain shrink-0"
            style={{ imageRendering: "-webkit-optimize-contrast" }}
            loading="lazy"
            decoding="async"
          />
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              From our sister team · Kinvanta by PaxBond
            </div>
            <div className="mt-1 font-display text-lg font-semibold text-foreground">
              Need in-person support too?
            </div>
            <p className="mt-1 text-sm text-muted-foreground max-w-xl leading-relaxed">
              Kinvanta is preparing a limited soft launch for families who need caregiver coordination, scheduling help, shared family dashboards, and trusted support tools. Join the Priority Early Access List to receive launch updates and first access when openings begin.
            </p>
          </div>
          <Button
            onClick={() => { setOpen(true); track(EVENTS.KINVANTA_WAITLIST_STARTED, { source }); }}
            variant="outline"
            className="rounded-full border-2"
            data-testid={`kinvanta-join-${source}`}
          >
            Join Priority List <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
        <KinvantaDialog open={open} onOpenChange={setOpen} source={source} />
      </>
    );
  }

  // sidecard / module
  return (
    <>
      <Card
        className="rounded-3xl border-border/60 soft-shadow bg-gradient-to-br from-white to-accent/30"
        data-testid={`kinvanta-card-${source}`}
      >
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <img
              src={KINVANTA_LOGO_URL}
              alt="Kinvanta"
              className="h-8 w-auto object-contain"
              style={{ imageRendering: "-webkit-optimize-contrast" }}
              loading="lazy"
              decoding="async"
            />
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              by PaxBond · soft launch
            </div>
          </div>
          <h3 className="mt-4 font-display text-base font-semibold text-foreground">
            Need in-person support too?
          </h3>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
            Kinvanta is preparing a limited soft launch for caregiver coordination, scheduling, and shared family dashboards. Join the Priority Early Access List for launch updates.
          </p>
          <Button
            onClick={() => { setOpen(true); track(EVENTS.KINVANTA_WAITLIST_STARTED, { source }); }}
            variant="outline"
            className="mt-4 rounded-full w-full border-2"
            data-testid={`kinvanta-join-${source}`}
          >
            Join Priority List <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </CardContent>
      </Card>
      <KinvantaDialog open={open} onOpenChange={setOpen} source={source} />
    </>
  );
}

/** Small inline CTA used inside TestCallCard post-success / blocked states. */
export function KinvantaInlineCTA({ source, emailFill, nameFill, phoneFill }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="rounded-2xl border border-border/60 bg-accent/30 p-4 flex items-center gap-3">
        <img
          src={KINVANTA_LOGO_URL}
          alt="Kinvanta"
          className="h-6 w-auto object-contain shrink-0"
          style={{ imageRendering: "-webkit-optimize-contrast" }}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">Need in-person support too?</div>
          <div className="text-xs text-muted-foreground">Kinvanta Priority List — soft launch.</div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full"
          data-testid={`kinvanta-inline-${source}`}
          onClick={() => { setOpen(true); track(EVENTS.KINVANTA_WAITLIST_STARTED, { source }); }}
        >
          Join
        </Button>
      </div>
      <KinvantaDialog
        open={open}
        onOpenChange={setOpen}
        source={source}
        initial={{ email: emailFill, first_name: nameFill, phone: phoneFill }}
      />
    </>
  );
}

function KinvantaDialog({ open, onOpenChange, source, initial = {} }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(initial.email || user?.email || "");
  const [firstName, setFirstName] = useState(initial.first_name || (user?.name || "").split(" ")[0] || "");
  const [phone, setPhone] = useState(initial.phone || user?.mobile || "");
  const [relation, setRelation] = useState("Parent");
  const [need, setNeed] = useState("Unsure");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async () => {
    if (!email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/kinvanta/waitlist", {
        email, first_name: firstName, phone, relation, biggest_need: need, source_page: source,
      });
      if (data.duplicate) {
        setDone({ duplicate: true, message: data.message });
        track(EVENTS.KINVANTA_WAITLIST_DUPLICATE, { source });
      } else {
        setDone({ duplicate: false, message: data.message });
        track(EVENTS.KINVANTA_WAITLIST_JOINED, { source, relation, biggest_need: need });
        toast.success("You're on the list!");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { onOpenChange(false); setTimeout(() => setDone(null), 200); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="kinvanta-dialog">
        {done === null ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <img src={KINVANTA_LOGO_URL} alt="Kinvanta" className="h-6 w-auto object-contain" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">by PaxBond</span>
              </div>
              <DialogTitle className="font-display text-xl pt-2">Join the Kinvanta Priority List</DialogTitle>
              <DialogDescription>
                Kinvanta is preparing a limited soft launch for caregiver coordination, scheduling, and shared family dashboards. Get launch updates and first access.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label htmlFor="kv-email">Email<span className="text-destructive">*</span></Label>
                <Input id="kv-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="mt-1.5 h-11 rounded-xl" data-testid="kinvanta-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="kv-name" className="text-muted-foreground text-xs">First name</Label>
                  <Input id="kv-name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                         className="mt-1.5 h-10 rounded-xl" data-testid="kinvanta-name-input" />
                </div>
                <div>
                  <Label htmlFor="kv-phone" className="text-muted-foreground text-xs">Phone</Label>
                  <Input id="kv-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                         className="mt-1.5 h-10 rounded-xl" data-testid="kinvanta-phone-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Relation to loved one</Label>
                  <Select value={relation} onValueChange={setRelation}>
                    <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="kinvanta-relation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KINVANTA_RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Biggest need</Label>
                  <Select value={need} onValueChange={setNeed}>
                    <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="kinvanta-need">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KINVANTA_NEEDS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button variant="ghost" onClick={close} className="rounded-full">Cancel</Button>
              <Button onClick={submit} disabled={submitting} className="rounded-full" data-testid="kinvanta-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Join Priority List
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="text-center py-4" data-testid="kinvanta-result">
            <div className="h-14 w-14 rounded-full bg-secondary/15 grid place-items-center mx-auto">
              <CheckCircle2 className="h-7 w-7 text-secondary" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">
              {done.duplicate ? "You're already on the list" : "You're on the list"}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">{done.message}</p>
            <Button onClick={close} className="mt-5 rounded-full" variant="outline">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
